/**
 * ThemeSwitcher — TopBar swatch picker for the 10 global color themes
 * plus a light/dark mode toggle. Selecting a theme applies instantly
 * (via ColorThemeProvider → data-color-theme) and persists
 * (localStorage immediately + settings-service cross-device).
 */

import { useState } from 'react'
import { Check, Moon, Palette, Sun, Monitor } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { useTheme } from '@/components/providers/ThemeProvider'
import { useColorTheme } from '@/components/providers/ColorThemeProvider'
import { themeList } from '@/lib/themes'

interface ThemeSwitcherProps {
  align?: 'start' | 'center' | 'end'
}

/** Light / Dark / Auto (system) — mirrors the client portal picker. */
const MODES = [
  { value: 'light' as const, icon: Sun, label: 'Clair' },
  { value: 'dark' as const, icon: Moon, label: 'Sombre' },
  { value: 'system' as const, icon: Monitor, label: 'Auto' },
]

export function ThemeSwitcher({ align = 'end' }: ThemeSwitcherProps) {
  const [open, setOpen] = useState(false)
  const { theme, setTheme } = useTheme()
  const { colorTheme, setColorTheme } = useColorTheme()

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0 rounded-full text-[hsl(var(--topbar-fg))] hover:bg-white/10 hover:text-[hsl(var(--topbar-fg))]"
                aria-label="Thème"
              >
                <Palette className="h-3.5 w-3.5" />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">Apparence & thème</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <PopoverContent align={align} sideOffset={8} className="w-[300px] p-3">
        {/* Light / Dark / Auto (system) */}
        <div className="mb-3">
          <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            Mode
          </p>
          <div className="grid grid-cols-3 gap-1.5">
            {MODES.map((m) => {
              const Icon = m.icon
              const active = theme === m.value
              return (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => setTheme(m.value)}
                  className={cn(
                    'flex flex-col items-center gap-1 rounded-lg border px-2 py-2 text-[11px] font-semibold transition-colors',
                    active
                      ? 'border-primary/50 bg-primary/10 text-primary ring-1 ring-primary/30'
                      : 'border-border hover:bg-accent',
                  )}
                >
                  <Icon className="h-4 w-4" /> {m.label}
                </button>
              )
            })}
          </div>
        </div>

        {/* Color theme swatches */}
        <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
          Thème de couleur
        </p>
        <ul className="grid grid-cols-2 gap-1.5">
          {themeList.map((t) => {
            const isActive = t.name === colorTheme
            return (
              <li key={t.name}>
                <button
                  type="button"
                  onClick={() => setColorTheme(t.name)}
                  title={t.description}
                  className={cn(
                    'group flex w-full items-center gap-2 rounded-lg border px-2 py-1.5 text-left transition-all',
                    isActive
                      ? 'border-primary/50 bg-primary/5'
                      : 'border-transparent hover:border-border hover:bg-accent/40',
                  )}
                >
                  <span
                    className="relative flex h-6 w-6 shrink-0 items-center justify-center rounded-full shadow-sm ring-1 ring-black/10"
                    style={{
                      background: `linear-gradient(135deg, ${t.swatch} 0%, ${t.accent} 100%)`,
                    }}
                  >
                    {isActive && <Check className="h-3.5 w-3.5 text-white drop-shadow" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className={cn(
                      'block truncate text-[12px] font-semibold',
                      isActive ? 'text-primary' : 'text-foreground',
                    )}>
                      {t.label}
                    </span>
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      </PopoverContent>
    </Popover>
  )
}
